import type { PlatformName } from '../api/types.js';

/**
 * How a platform's name is written for a human.
 *
 * ==================== WHY THIS FILE EXISTS ====================
 * Found during the Phase E consistency pass, 2026-09-15. The Console held
 * FOUR representations of this:
 *
 *   - `ChatMessage.tsx`, a module-private table;
 *   - `AlertsPage.tsx`, the same table again;
 *   - `ModerationCapabilities.tsx`, the same table a third time;
 *   - and the Widgets module, which could not reach any of them because all
 *     three were private, so it approximated with `text-transform: capitalize`
 *     over the raw platform id.
 *
 * The three tables agreed. The CSS did not, and could not: `youtube`
 * capitalises to "Youtube" and `tiktok` to "Tiktok". The Widgets editor shipped
 * a misspelt brand name on a control an operator toggles, which is the kind of
 * thing that makes a product look unfinished in exactly one glance.
 *
 * Phase A made this argument about the focus ring — "duplication is what makes
 * a PARTIAL change possible, and a ring correct in nine places is a defect in
 * the tenth". This is the same shape, and it had already produced its tenth.
 *
 * ==================== WHY NOT CSS ====================
 * `text-transform` cannot know that YouTube has a capital T, that TikTok has a
 * capital T in the middle, or that X is a single letter. A brand name is data,
 * not typography. Any surface showing a platform to a human reads it from
 * here.
 * =============================================================================
 */
export const PLATFORM_LABELS: Record<PlatformName, string> = {
  twitch: 'Twitch',
  youtube: 'YouTube',
  kick: 'Kick',
  blaze: 'Blaze',
  discord: 'Discord',
  tiktok: 'TikTok',
  facebook: 'Facebook',
  x: 'X',
  rumble: 'Rumble',
  instagram: 'Instagram',
};

/**
 * The written name for a platform, falling back to the id.
 *
 * The fallback matters: a platform added to the API before this table is a
 * missing row, and rendering its id is honest. Rendering nothing would hide a
 * platform an operator has connected.
 */
export function platformLabel(platform: string): string {
  /*
   * Widened deliberately rather than cast to `PlatformName`. The cast claimed
   * the lookup always succeeds, which made the fallback below look like dead
   * code to the linter — while at runtime the argument is a string off the
   * wire that may name a platform this table has never heard of. The cast was
   * the lie; the fallback is the truth.
   */
  const labels: Readonly<Record<string, string | undefined>> = PLATFORM_LABELS;

  return labels[platform] ?? platform;
}
